import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable, Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { SupplierViewModal } from '../components/procurement/SupplierViewModal';
import { mockPRs } from '../data/mockData';
import { AlertCircleIcon, XIcon, EyeIcon } from 'lucide-react';
const mockRFPBidders = [{
  id: '1',
  supplierId: 'SUP-001',
  supplierName: 'TechCorp Solutions',
  invitationDate: '2023-10-18',
  submissionStatus: 'Submitted',
  technicalScore: 85,
  financialScore: 90,
  totalScore: 87.5,
  responseStatus: 'Evaluated',
  documents: ['rfp_response.pdf', 'technical_proposal.pdf'],
  quotedAmount: 45000,
  deliveryTimeline: '4 Weeks'
}, {
  id: '2',
  supplierId: 'SUP-002',
  supplierName: 'Global Logistics Inc',
  invitationDate: '2023-10-18',
  submissionStatus: 'Submitted',
  technicalScore: 78,
  financialScore: 82,
  totalScore: 80.0,
  responseStatus: 'Evaluated',
  documents: ['proposal.pdf'],
  quotedAmount: 52000,
  deliveryTimeline: '3 Weeks'
}, {
  id: '3',
  supplierId: 'SUP-003',
  supplierName: 'BuildIt Construction',
  invitationDate: '2023-10-18',
  submissionStatus: 'Pending',
  responseStatus: 'Pending',
  documents: []
}];
export function RFPList() {
  const navigate = useNavigate();
  const [isBidderModalOpen, setIsBidderModalOpen] = useState(false);
  const [selectedPR, setSelectedPR] = useState<any>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const data = mockPRs.filter(pr => pr.status === 'Approved' && (pr.procurementMethod === 'Direct Purchase' || pr.procurementMethod === 'Open Tender'));
  const handleOpenBidders = (pr: any) => {
    setSelectedPR(pr);
    setIsBidderModalOpen(true);
  };
  const handleViewSupplier = (supplier: any) => {
    setSelectedSupplier(supplier);
    setIsSupplierModalOpen(true);
  };
  const bidderColumns: Column<any>[] = [{
    key: 'supplierName',
    label: 'Supplier Name'
  }, {
    key: 'invitationDate',
    label: 'Invitation Date'
  }, {
    key: 'submissionStatus',
    label: 'Submission',
    render: item => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.submissionStatus === 'Submitted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-2217">
          {item.submissionStatus}
        </span>
  }, {
    key: 'technicalScore',
    label: 'Tech Score',
    render: item => <span className="text-sm" data-id="element-2218">{item.technicalScore ?? '—'}</span>
  }, {
    key: 'financialScore',
    label: 'Fin Score',
    render: item => <span className="text-sm" data-id="element-2219">{item.financialScore ?? '—'}</span>
  }, {
    key: 'totalScore',
    label: 'Total',
    render: item => <span className="text-sm font-semibold text-indigo-600" data-id="element-2220">
          {item.totalScore ?? '—'}
        </span>
  }];
  const columns: Column<any>[] = [{
    key: 'prNo',
    label: 'PR No',
    render: pr => <span className="font-medium text-indigo-600" data-id="element-2221">{pr.prNo}</span>
  }, {
    key: 'originator',
    label: 'Originator'
  }, {
    key: 'requisitionFor',
    label: 'Requisition For'
  }, {
    key: 'procurementMethod',
    label: 'Method'
  }, {
    key: 'total',
    label: 'Total Amount',
    render: pr => `$${pr.total.toLocaleString()}`
  }, {
    key: 'status',
    label: 'PR Status',
    render: pr => <StatusBadge status={pr.status} data-id="element-2222" />
  }, {
    key: 'rfpStatus',
    label: 'RFP Status',
    render: () => <StatusBadge status="Draft" data-id="element-2223" />
  }, {
    key: 'actions',
    label: 'Action',
    render: pr => <div className="flex items-center justify-end space-x-3" data-id="element-2224">
          <button onClick={() => handleOpenBidders(pr)} className="text-amber-600 hover:text-amber-900 font-medium text-sm" data-id="element-2225">
            Bidder
          </button>
          <button onClick={() => navigate(`/rfp/${pr.id}`)} className="text-indigo-600 hover:text-indigo-900 font-medium text-sm" data-id="element-2226">
            Process RFP
          </button>
        </div>
  }];
  return <div className="space-y-6 h-full flex flex-col pb-8" data-id="element-2227">
      <div className="flex items-center justify-between" data-id="element-2228">
        <div data-id="element-2229">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2230">
            Request for Proposal (RFP)
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2231">
            Process RFP for approved Direct Purchase and Open Tender
            requisitions.
          </p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-start" data-id="element-2232">
        <AlertCircleIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-2233" />
        <div className="text-sm text-blue-800 space-y-1" data-id="element-2234">
          <p data-id="element-2235">
            For <strong data-id="element-2236">Open Tender</strong> requisitions, ensure the EOI
            process is completed before initiating the RFP.
          </p>
          <p data-id="element-2237">
            For <strong data-id="element-2238">Short Purchase</strong> requisitions, ensure the RFQ
            process is completed before initiating the RFP.
          </p>
        </div>
      </div>

      <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col" data-id="element-2239">
        <div className="flex-1 p-0" data-id="element-2240">
          <DataTable columns={columns} data={data} keyExtractor={item => item.id} searchPlaceholder="Search PRs..." data-id="element-2241" />
        </div>
      </div>

      {/* Bidders Modal — Suppliers & Evaluation */}
      {isBidderModalOpen && selectedPR && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-2242">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0" data-id="element-2243">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setIsBidderModalOpen(false)} data-id="element-2244"></div>
            <div className="relative inline-block w-full max-w-5xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6" data-id="element-2245">
              <div className="absolute top-0 right-0 pt-4 pr-4" data-id="element-2246">
                <button className="text-gray-400 bg-white rounded-md hover:text-gray-500" onClick={() => setIsBidderModalOpen(false)} data-id="element-2247">
                  <XIcon className="w-6 h-6" data-id="element-2248" />
                </button>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-6" data-id="element-2249">
                Suppliers & Evaluation — {selectedPR.prNo}
              </h3>
              <DataTable columns={bidderColumns} data={mockRFPBidders} keyExtractor={item => item.id} searchPlaceholder="Search suppliers..." actions={item => <button onClick={() => handleViewSupplier(item)} className="inline-flex items-center text-indigo-600 hover:text-indigo-900 font-medium text-sm" data-id="element-2251">
                    <EyeIcon className="h-4 w-4 mr-1" data-id="element-2252" />
                    View
                  </button>} data-id="element-2250" />
            </div>
          </div>
        </div>}

      <SupplierViewModal isOpen={isSupplierModalOpen} onClose={() => setIsSupplierModalOpen(false)} supplier={selectedSupplier} type="RFP" data-id="element-2253" />
    </div>;
}